import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import TabNavigation from "../ui/TabNavigation";
import { AnimatePresence } from 'framer-motion';
import { toast } from '../../utils/notify';
import { useLocation } from "react-router-dom";
import {
  getDocumentFrameworksRoleBasedApi,
  getDocumentFrameworksEmployeeApi,
  getUserDocumentFrameworksApi
} from "../../services/axios/AuthApis/DocumentLibraryApis";
import { getBgvList, getPerformanceUsers } from "../../services/axios/AuthApis/TeamCollabApis";
import { createFrameworkApi, getCustomerFrameworkListApi, getCustomerMyLibraryApi, getFrameworkTypesApi } from "../../services/axios/AuthApis/TrustCenterApis";
import CustomizedFileApprovalView from './CustomizedFileApprovalView';
import MyLibrariesView from './MyLibrariesView';
import BgvView from './BgvView';
import PerformanceView from './PerformanceView';
import FrameworkModal from './FrameworkModal';
import DocumentLibraryMasterView from './DocumentLibraryMasterView';
import {
  DocumentTextIcon,
  FolderIcon,
  BuildingLibraryIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { useSelector } from 'react-redux';


const DocumentLibrary = () => {
  const { user } = useAuth();

  const [frameworkLoading, setFrameworkLoading] = useState(true);
  const [activeView] = useState('interactive');
  const [activeTab, setActiveTab] = useState('dl_master');
  const [frameworks, setFrameworks] = useState([]);
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [showFrameworkModal, setShowFrameworkModal] = useState(false);
  const [openDocumentId, setOpenDocumentId] = useState(null);
  const [frameworkTypes, setFrameworkTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [myLibraries, setMyLibraries] = useState([]);
  const [myLibrariesLoading, setMyLibrariesLoading] = useState(false);
  const [myLibrariesPagination, setMyLibrariesPagination] = useState({
    page: 1,
    page_size: 20,
    total_pages: 1,
    count: 0,
  });
  const [myLibrariesFilters, setMyLibrariesFilters] = useState({
    search: "",
    policy: "",
    title: "",
  });
  const [bgvRecords, setBgvRecords] = useState([]);
  const [bgvLoading, setBgvLoading] = useState(false);
  const [bgvPagination, setBgvPagination] = useState({
    page: 1,
    page_size: 20,
    total_pages: 1,
    count: 0,
  });
  const [bgvFilters, setBgvFilters] = useState({
    employee: "",
    department: "",
    doj: "",
  });
  const [performanceRecords, setPerformanceRecords] = useState([]);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [performancePagination, setPerformancePagination] = useState({
    page: 1,
    page_size: 20,
    total_pages: 1,
    count: 0,
  });
  const [frameworkFormData, setFrameworkFormData] = useState({
    name: "",
    type: "",
    category: "",
    country: "",
    description: ""
  });
  const [filters, setFilters] = useState({
    type: "",
    category: "",
    country: "",
    status: ""
  });
  const [frameworkPagination, setFrameworkPagination] = useState({
    page: 1,
    page_size: 20,
    total_pages: 1,
    count: 0
  });

  const selectedCompany = useSelector((state) => state.company.selectedCompany);

  const resetFrameworkForm = () => {
    setFrameworkFormData({
      name: "",
      type: "",
      category: "",
      country: "",
      region: "",
      industry: "",
      description: "",
      version: "",
    });
  };

  const hasActiveMyLibrariesFilters =
    !!myLibrariesFilters.search ||
    !!myLibrariesFilters.policy ||
    !!myLibrariesFilters.title;

  const hasActiveBgvFilters =
    !!bgvFilters.employee ||
    !!bgvFilters.department ||
    !!bgvFilters.doj;

  let customer_id;
  if (user?.role === "customer_admin") {
    customer_id = user.id;
  } else if ((user?.role === "security_analyst" || user?.role === "auditor") && selectedCompany) {
    customer_id = selectedCompany?.user_id;
  } else if (user?.role === "end_user") {
    customer_id = user?.customer_master_id || user?.customer_id || "";
  }

  const isCustomerAdmin = user?.role === "customer_admin";
  const isPlatformAdmin = user?.role === "platform_admin";
  const isAdmin = user?.role === "admin";
  const isSecurityAnalyst = user?.role === "security_analyst" && selectedCompany ? true : false;
  const isAuditor = user?.role === "auditor" && selectedCompany ? true : false;
  const customerMasterId =
    user?.customer_master_id ||
    user?.customer_id ||
    selectedCompany?.id ||
    "";

  useEffect(() => {
    setFrameworkPagination(p => ({ ...p, page: 1 }));
  }, [filters]);

  useEffect(() => {
    loadFrameworks();
  }, [frameworkPagination.page, frameworkPagination.page_size, filters]);

  const location = useLocation();

  useEffect(() => {
    if (location.state?.openTab === "bgv") setActiveTab("bgv");
    if (location.state?.openTab === "performance_record") setActiveTab("performance_record");
    if (location.state?.openDocumentId) {
      setActiveTab("dl_master");
      setOpenDocumentId(location.state.openDocumentId);
    }
  }, [location.state]);

  // useEffect(() => {
  //   if (activeTab === "my_libraries") loadMyLibraries(myLibrariesPagination.page);
  // }, [activeTab, myLibrariesPagination.page, customer_id]);

  useEffect(() => {
    if (activeTab === "bgv") loadBgvRecords(bgvPagination.page);
  }, [activeTab, bgvPagination.page, bgvPagination.page_size, customerMasterId]);

  useEffect(() => {
    if (activeTab === "performance_record") loadPerformanceRecords(performancePagination.page);
  }, [activeTab, performancePagination.page, performancePagination.page_size, customerMasterId, selectedCompany?.id]);

  useEffect(() => {
    const loadFrameworkTypes = async () => {
      try {
        const data = await getFrameworkTypesApi();
        console.log({data: data})
        setFrameworkTypes(data?.frameworks || []);
        setCategories(data?.categories || []);
        setCountries(data?.countries || []);
      } catch (err) {
        console.error("Failed to load framework types", err);
      }
    };
    loadFrameworkTypes();
  }, []);

  useEffect(() => {
    if (isPlatformAdmin) setActiveTab("dl_master");
  }, [user]);

  const handleCreateFramework = async () => {
    try {
      if (!frameworkFormData.name || !frameworkFormData.type) {
        toast.error("Required fields missing");
        return;
      }
      await createFrameworkApi(frameworkFormData);
      await loadFrameworks();
      toast.success("Framework created successfully");
      setShowFrameworkModal(false);
      setFrameworkFormData({ name: "", type: "", category: "", country: "", description: "" });
    } catch (error) {
      toast.error("Failed to create framework");
    }
  };

  const loadFrameworks = async () => {
    setFrameworkLoading(true);
    try {
      const query = buildQuery();
      let res;
      if (user?.role === "customer_admin") {
        const response = await getCustomerFrameworkListApi(customer_id);
        res = {
          count: response.framework.filter(item => item.enabled).length || 0,
          results: response?.framework.filter((item) => item.enabled) || []
        };
      } else if ((user?.role === "security_analyst" || user?.role === "auditor") && selectedCompany) {
        const response = await getCustomerFrameworkListApi(customer_id);
        res = {
          count: response.framework.filter(item => item.enabled).length || 0,
          results: response?.framework.filter((item) => item.enabled) || []
        };
      } else if (user?.role === "end_user") {
        res = await getUserDocumentFrameworksApi(
          frameworkPagination.page,
          frameworkPagination.page_size,
          user?.id,
          query
        );
      } else {
        res = await getDocumentFrameworksRoleBasedApi(
          frameworkPagination.page,
          frameworkPagination.page_size,
          query
        );
      }
      setFrameworks(res.results);
      setFrameworkPagination(p => ({
        ...p,
        total_pages: res.total_pages,
        count: res.count
      }));
    } catch (error) {
      console.error("Failed to load document frameworks", error);
      setFrameworks([]);
      setFrameworkPagination((p) => ({ ...p, total_pages: 1, count: 0 }));
      if (error?.code === "ERR_NETWORK") {
        toast.error("Unable to reach backend service.", { id: "document-frameworks-network-error" });
      } else {
        toast.error("Failed to load frameworks", { id: "document-frameworks-load-error" });
      }
    } finally {
      setFrameworkLoading(false);
    }
  };

  // const loadMyLibraries = async (page = 1) => {
  //   try {
  //     setMyLibrariesLoading(true);
  //     const res = await getCustomerMyLibraryApi(
  //       page,
  //       myLibrariesPagination.page_size,
  //       { customer_id: customer_id || "" }
  //     );
  //     setMyLibraries(res.results || []);
  //     setMyLibrariesPagination((prev) => ({
  //       ...prev,
  //       page: res.current_page || page,
  //       total_pages: res.total_pages || 1,
  //       count: res.count || 0,
  //     }));
  //   } catch (error) {
  //     console.error("Failed to load my libraries", error);
  //     setMyLibraries([]);
  //   } finally {
  //     setMyLibrariesLoading(false);
  //   }
  // };

  const loadBgvRecords = async (page = 1) => {
    try {
      setBgvLoading(true);
      const res = await getBgvList({
        page,
        page_size: bgvPagination.page_size,
        customer_master_id: customerMasterId || undefined,
      });
      setBgvRecords(res.results || []);
      setBgvPagination((prev) => ({
        ...prev,
        page: res.current_page || page,
        total_pages: res.total_pages || 1,
        count: res.count || 0,
      }));
    } catch (error) {
      console.error("Failed to load BGV records", error);
      setBgvRecords([]);
    } finally {
      setBgvLoading(false);
    }
  };

  const loadPerformanceRecords = async (page = 1) => {
    try {
      setPerformanceLoading(true);
      const scopedCustomerMasterId = user?.role === "auditor"
        ? undefined
        : customerMasterId || user?.customer_master_id || user?.customer_id;
      const res = await getPerformanceUsers({
        customer_master_id: scopedCustomerMasterId,
        page,
        page_size: performancePagination.page_size,
      });
      setPerformanceRecords(res.results || []);
      setPerformancePagination((prev) => ({
        ...prev,
        page: res.current_page || page,
        total_pages: res.total_pages || 1,
        count: res.count || 0,
      }));
    } catch (error) {
      console.error("Failed to load performance records", error);
      setPerformanceRecords([]);
    } finally {
      setPerformanceLoading(false);
    }
  };

  const buildQuery = () => {
    const params = new URLSearchParams({
      page: frameworkPagination.page,
      page_size: frameworkPagination.page_size,
      ...filters
    });
    Object.keys(filters).forEach(k => !filters[k] && params.delete(k));
    return params.toString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed": return "text-green-700 bg-green-100 border-green-300";
      case "Pending": return "text-red-700 bg-red-100 border-red-300";
      default: return "text-gray-600 bg-gray-100 border-gray-300";
    }
  };

  const getBgvStatusColor = (status) => {
    return status === 1 ? "text-green-700 bg-green-100" : "text-red-700 bg-red-100";
  };

  const filteredBgvRecords = useMemo(() => {
    let list = [...bgvRecords];
    if (bgvFilters.employee) {
      list = list.filter((r) => String(r.firstname || "").toLowerCase().includes(bgvFilters.employee.toLowerCase()));
    }
    if (bgvFilters.department) {
      list = list.filter((r) => String(r.department || "").toLowerCase().includes(bgvFilters.department.toLowerCase()));
    }
    if (bgvFilters.doj) {
      list = list.filter((r) => r.date_of_joining && String(r.date_of_joining).startsWith(bgvFilters.doj));
    }
    return list;
  }, [bgvRecords, bgvFilters]);

  const myLibraryPolicyOptions = useMemo(() => {
    return Array.from(new Set((myLibraries || []).map((item) => String(item?.type || "").trim()).filter(Boolean)));
  }, [myLibraries]);

  const filteredMyLibraries = useMemo(() => {
    let list = [...myLibraries];
    if (myLibrariesFilters.search) {
      const q = myLibrariesFilters.search.toLowerCase();
      list = list.filter((item) =>
        [item?.title, item?.type, item?.description].map((v) => String(v || "").toLowerCase()).some((v) => v.includes(q))
      );
    }
    if (myLibrariesFilters.policy) {
      list = list.filter((item) => String(item?.type || "").toLowerCase() === myLibrariesFilters.policy.toLowerCase());
    }
    if (myLibrariesFilters.title) {
      list = list.filter((item) => String(item?.title || "").toLowerCase().includes(myLibrariesFilters.title.toLowerCase()));
    }
    return list;
  }, [myLibraries, myLibrariesFilters]);

  let tabs;
  if (isPlatformAdmin || isAdmin) {
    tabs = [{ id: 'dl_master', name: 'Document Library Master', icon: BuildingLibraryIcon }];
  } else if (isCustomerAdmin || isSecurityAnalyst) {
    tabs = [
      { id: 'dl_master', name: 'Document Library', icon: BuildingLibraryIcon },
      { id: 'my_libraries', name: 'My libraries', icon: DocumentTextIcon },
      { id: 'bgv', name: 'BGV record', icon: FolderIcon },
      { id: 'performance_record', name: 'Performance record', icon: ClipboardDocumentListIcon },    
      { id: 'customized_file_approval', name: 'Customized File Approval', icon: ClipboardDocumentListIcon}
    ];
  } else {
    const includeMyLibraries = user?.role === "end_user" || isAuditor;
    const myLibrariesTab = includeMyLibraries
      ? [{ id: 'my_libraries', name: 'My libraries', icon: DocumentTextIcon }]
      : [];
 
    tabs = [
      { id: 'dl_master', name: 'Document Library', icon: BuildingLibraryIcon },
      ...myLibrariesTab,
      { id: 'bgv', name: 'BGV record', icon: FolderIcon },
      { id: 'performance_record', name: 'Performance record', icon: ClipboardDocumentListIcon }
    ];
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 max-w-full mx-auto  min-h-screen shadow-md">
      <div className="mb-6 md:mb-8">
        <h1 className="text-xl md:text-3xl font-bold text-gray-900">Document Library</h1>
        <p className="text-xs md:text-sm text-gray-600 mt-1">
          Comprehensive policy and procedure repository `with AI-generated content
        </p>
      </div>

      <TabNavigation
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(id) => {
          setActiveTab(id);
          if (id === "dl_master") setSelectedFramework(null);
        }}
      />

      <div className="flex items-center justify-between mb-6">
        <div />
        <div className="flex items-center gap-4" />
      </div>

      <AnimatePresence mode="wait">
        {activeView === "interactive" && activeTab === "my_libraries" && (
          <MyLibrariesView 
          customerId={customer_id}
          />
        )}
        {activeView === "interactive" && activeTab === "bgv" && (
          <BgvView
            bgvLoading={bgvLoading}
            filteredBgvRecords={filteredBgvRecords}
            bgvFilters={bgvFilters}
            setBgvFilters={setBgvFilters}
            hasActiveBgvFilters={hasActiveBgvFilters}
            bgvPagination={bgvPagination}
            setBgvPagination={setBgvPagination}
            getBgvStatusColor={getBgvStatusColor}
            user={user}
          />
        )}
        {activeView === "interactive" && activeTab === "performance_record" && (
          <PerformanceView
            performanceLoading={performanceLoading}
            performanceRecords={performanceRecords}
            performancePagination={performancePagination}
            setPerformancePagination={setPerformancePagination}
          />
        )}
        {activeView === 'interactive' && activeTab === 'dl_master' && (
          <DocumentLibraryMasterView
            frameworks={frameworkTypes}
            userRole={user?.role}
            customer_id={customer_id}
            selectedFramework={selectedFramework}
            setSelectedFramework={setSelectedFramework}
            openDocumentId={openDocumentId}
            onDocumentOpened={() => setOpenDocumentId(null)}
          />
        )}
         {activeView === 'interactive' && activeTab === 'customized_file_approval' && (
          <CustomizedFileApprovalView
            customer_id={customer_id}
            userRole={user?.role}
            frameworks={frameworks}
          />
        )}
      </AnimatePresence>

      <FrameworkModal
        showFrameworkModal={showFrameworkModal}
        setShowFrameworkModal={setShowFrameworkModal}
        frameworkFormData={frameworkFormData}
        setFrameworkFormData={setFrameworkFormData}
        frameworkTypes={frameworkTypes}
        categories={categories}
        countries={countries}
        handleCreateFramework={handleCreateFramework}
        resetFrameworkForm={resetFrameworkForm}
      />
    </div>
  );
};

export default DocumentLibrary;
